import React, { useEffect, useState, useMemo } from "react";
import api, { 
  salesServicesApi, 
  crmApi, 
  salaryPaymentsApi, 
  otherExpensesApi,
  taskPaymentsApi,
  statsApi 
} from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Target,
  ChevronLeft,
  ChevronRight,
  Building2,
  Wallet,
  Users,
  Briefcase,
  Calculator,
  Palette,
  RefreshCw,
  CheckCircle2,
  Clock,
  Percent,
  Activity,
  Megaphone,
} from "lucide-react";
import {
  ResponsiveContainer,
  ComposedChart,
  AreaChart,
  Area,
  Line,
  Bar,
  BarChart,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const FULL_MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const GeneralPnlPage = () => {
  const { rates } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().getMonth() + 1);
  const [activeTab, setActiveTab] = useState("sales");
  const [chartView, setChartView] = useState("fact"); // "plan" | "fact"
  const [byManager, setByManager] = useState(false); // income breakdown by sales manager
  const [excludePartner, setExcludePartner] = useState(false); // hide partner (owner) income everywhere
  
  // Backend-calculated summary (single source of truth)
  const [dashboardData, setDashboardData] = useState(null);
  
  // Raw data for drill-down tabs
  const [salesServices, setSalesServices] = useState([]);
  const [crmPayments, setCrmPayments] = useState([]);
  const [salaryPayments, setSalaryPayments] = useState([]);
  const [otherExpenses, setOtherExpenses] = useState([]);
  const [taskPayments, setTaskPayments] = useState([]);
  
  const usdToUah = rates?.USD || 42.97;
  const eurToUah = rates?.EUR || 50.91;

  const currentMonthKey = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}`;

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [dashRes, salesRes, crmRes, salaryRes, expensesRes, taskRes] = await Promise.all([
        statsApi.getOwnerDashboard("all", currentMonthKey).catch(() => ({ data: null })),
        salesServicesApi.getAll().catch(() => ({ data: [] })),
        crmApi.getPayments({}).catch(() => ({ data: [] })),
        salaryPaymentsApi.getAll({}).catch(() => ({ data: [] })),
        otherExpensesApi.getAll({}).catch(() => ({ data: [] })),
        taskPaymentsApi.getAll({}).catch(() => ({ data: [] })),
      ]);
      
      setDashboardData(dashRes.data);
      setSalesServices(salesRes.data || []);
      setCrmPayments(crmRes.data || []);
      setSalaryPayments(salaryRes.data || []);
      setOtherExpenses(expensesRes.data || []);
      setTaskPayments(taskRes.data || []);
      
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [currentMonthKey]);

  // Convert to USD
  const toUSD = (amount, currency) => {
    if (!amount || isNaN(amount)) return 0;
    const cur = (currency || "UAH").toUpperCase();
    if (cur === "USD") return Number(amount);
    if (cur === "EUR") return Number(amount) * (eurToUah / usdToUah);
    return Number(amount) / usdToUah;
  };

  const formatUSD = (amountUSD) => {
    if (!amountUSD || isNaN(amountUSD)) return "$0";
    return `$${Math.round(amountUSD).toLocaleString()}`;
  };

  const getMonthKey = (dateStr) => {
    if (!dateStr) return null;
    if (dateStr.match(/^\d{4}-\d{2}$/)) return dateStr;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  };

  // Month a RECEIVED payment belongs to — mirrors income_fact_month() on the
  // backend. `month` is the month the deal was booked for and never moves when
  // the client finally pays, so fact buckets follow paid_date (sales_services)
  // / payment_date (crm_payments). Both are stored ISO and legacy DD.MM.YYYY.
  // Falls back to `month` when the payment date is missing.
  const factMonthKey = (rec) => {
    const pd = rec.paid_date || rec.payment_date;
    if (pd) {
      const iso = String(pd).match(/^(\d{4})-(\d{2})/);
      if (iso) return `${iso[1]}-${iso[2]}`;
      const legacy = String(pd).match(/^(\d{2})\.(\d{2})\.(\d{4})/);
      if (legacy) return `${legacy[3]}-${legacy[2]}`;
    }
    return rec.month || null;
  };

  // Summary from backend (single source of truth — matches Owner Dashboard)
  const rawSummary = dashboardData?.summary || {};
  const expBreakdown = dashboardData?.expenses_breakdown || {};
  const partnerManagerNames = dashboardData?.partner_manager_names || [];

  // "Exclude partner income" view: subtract the partner (owner) sub-slice from
  // sales/income and recompute profit + margin. Expenses are untouched — the
  // point is to show what the sales team brings in vs what the company spends.
  const summary = useMemo(() => {
    if (!excludePartner) return rawSummary;
    // partner_* comes on summary (new backend) with a monthly_breakdown
    // fallback so the toggle also works against an older backend build
    const monthRow = (dashboardData?.monthly_breakdown || []).find((r) => r.month === currentMonthKey) || {};
    const partnerPlan = rawSummary.partner_plan ?? monthRow.partner_plan ?? 0;
    const partnerFact = rawSummary.partner_fact ?? monthRow.partner_fact ?? 0;
    const income_plan = (rawSummary.income_plan || 0) - partnerPlan;
    const income_fact = (rawSummary.income_fact || 0) - partnerFact;
    const profit_plan = income_plan - (rawSummary.expenses_plan || 0);
    const profit_fact = income_fact - (rawSummary.expenses_fact || 0);
    return {
      ...rawSummary,
      sales_plan: (rawSummary.sales_plan || 0) - partnerPlan,
      sales_fact: (rawSummary.sales_fact || 0) - partnerFact,
      income_plan,
      income_fact,
      profit_plan,
      profit_fact,
      margin: income_fact > 0 ? (profit_fact / income_fact) * 100 : 0,
    };
  }, [rawSummary, excludePartner, dashboardData, currentMonthKey]);

  // 6-month period iterator (helper)
  const lastSixMonths = useMemo(() => {
    const out = [];
    for (let offset = 5; offset >= 0; offset--) {
      let m = selectedMonth - offset;
      let y = selectedYear;
      while (m <= 0) {
        m += 12;
        y -= 1;
      }
      out.push({ y, m, key: `${y}-${String(m).padStart(2, "0")}`, label: MONTH_NAMES[m - 1] });
    }
    return out;
  }, [selectedYear, selectedMonth]);

  // Per-manager income breakdown — last 6 months × manager.
  //   Plan: ALL sales_services where s.month == period (booked for that month)
  //   Fact: sales_services paid in that period (factMonthKey), whatever month
  //         they were booked for — a July invoice settled in September belongs
  //         to September's cash.
  // For CRM payments same rule: c.month for plan, payment month + status=='paid'
  // for fact. Matches income_fact_month() in /stats/owner-dashboard.
  const incomeByManager = useMemo(() => {
    const partnerSet = new Set(excludePartner ? partnerManagerNames : []);
    const managers = new Set();
    salesServices.forEach((s) => {
      const mgr = s.sales_manager_name || "Unassigned";
      if (!partnerSet.has(mgr)) managers.add(mgr);
    });
    const managerList = Array.from(managers).sort();

    const rows = lastSixMonths.map(({ key, label }) => {
      const row = { month: label, month_key: key };
      managerList.forEach((m) => {
        row[m] = 0;
      });
      row["CRM (recurring)"] = 0;
      salesServices.forEach((s) => {
        if (chartView === "fact") {
          if (!s.is_paid || factMonthKey(s) !== key) return;
        } else if (s.month !== key) return;
        const mgr = s.sales_manager_name || "Unassigned";
        if (partnerSet.has(mgr)) return;
        row[mgr] += toUSD(s.amount, s.currency);
      });
      crmPayments.forEach((c) => {
        if (chartView === "fact") {
          if (c.status !== "paid" || factMonthKey(c) !== key) return;
        } else if (c.month !== key) return;
        const amt = chartView === "fact"
          ? (c.actual_amount || c.planned_amount || 0)
          : (c.planned_amount || 0);
        row["CRM (recurring)"] += toUSD(amt, c.currency);
      });
      managerList.forEach((m) => {
        row[m] = Math.round(row[m]);
      });
      row["CRM (recurring)"] = Math.round(row["CRM (recurring)"]);
      return row;
    });
    return { rows, managers: managerList };
  }, [salesServices, crmPayments, lastSixMonths, chartView, usdToUah, eurToUah, excludePartner, partnerManagerNames]);

  // Color palette for manager stacks (cycles if more than 10 managers)
  const MANAGER_COLORS = [
    "#10b981", "#06b6d4", "#3b82f6", "#a78bfa", "#ec4899",
    "#f97316", "#eab308", "#84cc16", "#14b8a6", "#f43f5e",
  ];

  // Last 6 months ending with selected — chart data from monthly_breakdown
  // (same trusted source as cards). For each month we expose plan + fact
  // breakdowns of income (sales + CRM) and expenses (salary + artists + AD + other).
  const cashFlow6m = useMemo(() => {
    const all = dashboardData?.monthly_breakdown || [];
    const byMonth = Object.fromEntries(all.map((r) => [r.month, r]));
    const out = [];
    for (let offset = 5; offset >= 0; offset--) {
      let m = selectedMonth - offset;
      let y = selectedYear;
      while (m <= 0) {
        m += 12;
        y -= 1;
      }
      const key = `${y}-${String(m).padStart(2, "0")}`;
      const row = byMonth[key] || {};
      out.push({
        month: MONTH_NAMES[m - 1],
        month_key: key,
        // income plan/fact + components
        // partner_* is a sub-slice of sales_* in the API — subtract here so the
        // stacked segments (Sales w/o partner + Partner) add up to total sales
        sales_plan: Math.round((row.sales_plan || 0) - (row.partner_plan || 0)),
        sales_fact: Math.round((row.sales_fact || 0) - (row.partner_fact || 0)),
        partner_plan: Math.round(row.partner_plan || 0),
        partner_fact: Math.round(row.partner_fact || 0),
        crm_plan: Math.round(row.crm_plan || 0),
        crm_fact: Math.round(row.crm_fact || 0),
        support_plan: Math.round(row.support_plan || 0),
        support_fact: Math.round(row.support_fact || 0),
        income_plan: Math.round(row.income_plan || 0),
        income_fact: Math.round(row.income_fact || 0),
        // income with the partner (owner) slice stripped — used by the
        // "exclude partner income" toggle
        income_plan_np: Math.round((row.income_plan || 0) - (row.partner_plan || 0)),
        income_fact_np: Math.round((row.income_fact || 0) - (row.partner_fact || 0)),
        // expense plan/fact + components
        salary_plan: Math.round(row.salary_plan || 0),
        salary_fact: Math.round(row.salary_fact || 0),
        artist_plan: Math.round(row.artist_plan || 0),
        artist_fact: Math.round(row.artist_fact || 0),
        ad_fee_plan: Math.round(row.ad_fee_plan || 0),
        ad_fee_fact: Math.round(row.ad_fee_fact || 0),
        other_plan: Math.round(row.other_plan || 0),
        other_fact: Math.round(row.other_fact || 0),
        ads_plan: Math.round(row.ads_plan || 0),
        ads_fact: Math.round(row.ads_fact || 0),
        bad_debt_plan: Math.round(row.bad_debt_plan || 0),
        expenses_plan: Math.round(row.expenses_plan || 0),
        expenses_fact: Math.round(row.expenses_fact || 0),
      });
    }
    return out;
  }, [dashboardData, selectedYear, selectedMonth]);

  // Detail data for drill-down tabs
  // Exclude support fees — they're shown as their own "Support" income stream
  // (summary.support_*) and live on /support-payments, so the Sales tab/card stay
  // in sync (support_monthly services would otherwise inflate the Sales drill-down).
  const salesRecords = useMemo(() => {
    // /sales-services attaches canonical sales_manager_id/name (project →
    // client → raw chain), so partner rows can be cut right here when the
    // exclude toggle is on — keeps the drill-down table in sync with the cards.
    const partnerIds = new Set(excludePartner ? (dashboardData?.partner_manager_ids || []) : []);
    const partnerNames = new Set(excludePartner ? partnerManagerNames : []);
    // Plan and fact now sit on different axes, so the drill-down is the union
    // of both: booked for this month (feeds PLAN) OR paid this month (feeds
    // FACT). Without the union a July deal settled in September would be
    // missing from the table the owner clicks into from the FACT card.
    return (salesServices || []).filter(s =>
      (s.month === currentMonthKey || (s.is_paid && factMonthKey(s) === currentMonthKey)) &&
      s.service_category !== "support" &&
      !partnerIds.has(s.sales_manager_id) &&
      !partnerNames.has(s.sales_manager_name)
    ).map(s => ({
      ...s,
      _inPlan: s.month === currentMonthKey && (s.probability || 0) >= 50,
      _inFact: !!s.is_paid && factMonthKey(s) === currentMonthKey,
      _paidMonth: s.is_paid ? factMonthKey(s) : null,
    }));
  }, [salesServices, currentMonthKey, excludePartner, dashboardData, partnerManagerNames]);

  const crmRecords = useMemo(() =>
    (crmPayments || [])
      .filter(p => p.month === currentMonthKey || (p.status === "paid" && factMonthKey(p) === currentMonthKey))
      .map(p => ({
        ...p,
        _inPlan: p.month === currentMonthKey,
        _inFact: p.status === "paid" && factMonthKey(p) === currentMonthKey,
        _paidMonth: p.status === "paid" ? factMonthKey(p) : null,
      })),
  [crmPayments, currentMonthKey]);

  const salaryRecords = useMemo(() => 
    (salaryPayments || []).filter(s => s.month === currentMonthKey),
  [salaryPayments, currentMonthKey]);

  // Artist detail data for the Artists tab
  const artistDetail = useMemo(() => {
    const artistExpenses = (otherExpenses || []).filter(e => e.expense_type === "artist_payment");
    const paidExternal = [];
    const pendingExternal = [];
    
    artistExpenses.forEach(e => {
      const status = e.status || "pending";
      if (status === "paid") {
        const paidMonth = getMonthKey(e.paid_date);
        if (paidMonth === currentMonthKey) paidExternal.push(e);
      } else {
        const dateMonth = getMonthKey(e.date);
        if (dateMonth === currentMonthKey) pendingExternal.push(e);
      }
    });
    
    const paidTask = [];
    const pendingTask = [];
    (taskPayments || []).filter(tp => tp.payment_type !== "art_director_fee").forEach(tp => {
      const status = tp.status || "pending";
      if (status === "paid") {
        const paidMonth = getMonthKey(tp.paid_date);
        if (paidMonth === currentMonthKey) paidTask.push(tp);
      } else if (status !== "cancelled") {
        const confirmedMonth = getMonthKey(tp.client_confirmed_date) || getMonthKey(tp.created_at);
        if (confirmedMonth === currentMonthKey) pendingTask.push(tp);
      }
    });

    // AD Fee records
    const paidAdFee = [];
    const pendingAdFee = [];
    (taskPayments || []).filter(tp => tp.payment_type === "art_director_fee").forEach(tp => {
      const status = tp.status || "pending";
      if (status === "paid") {
        const paidMonth = getMonthKey(tp.paid_date);
        if (paidMonth === currentMonthKey) paidAdFee.push(tp);
      } else if (status !== "cancelled") {
        const confirmedMonth = getMonthKey(tp.client_confirmed_date) || getMonthKey(tp.created_at);
        if (confirmedMonth === currentMonthKey) pendingAdFee.push(tp);
      }
    });
    
    return { paidExternal, pendingExternal, paidTask, pendingTask, paidAdFee, pendingAdFee };
  }, [otherExpenses, taskPayments, currentMonthKey]);

  // Other expenses detail
  const otherExpRecords = useMemo(() => {
    return (otherExpenses || []).filter(e => {
      if (e.expense_type === "artist_payment") return false;
      const expenseMonth = e.month || getMonthKey(e.date);
      return expenseMonth === currentMonthKey;
    });
  }, [otherExpenses, currentMonthKey]);

  // Income by service type (for breakdown chart)
  const incomeByServiceType = useMemo(() => {
    const types = {};
    salesRecords.forEach(s => {
      const stype = s.service_type || "Other";
      if (!types[stype]) types[stype] = { plan: 0, fact: 0 };
      const amountUSD = toUSD(s.amount, s.currency);
      if (s._inPlan) types[stype].plan += amountUSD;
      if (s._inFact) types[stype].fact += amountUSD;
    });
    return Object.entries(types).map(([name, data]) => ({ name, ...data })).sort((a, b) => b.plan - a.plan);
  }, [salesRecords, usdToUah, eurToUah]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="general-pnl-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div data-testid="general-pnl-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 flex items-center gap-3">
            <Calculator className="h-7 w-7 text-purple-400" />
            General P&L
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Plan vs Fact — All amounts in USD (backend-calculated)</p>
        </div>

        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={loadData} className="border-zinc-700" data-testid="pnl-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          
          <div className="flex items-center gap-2 bg-zinc-900 rounded-lg p-1 border border-zinc-800">
            <Button variant="ghost" size="icon" onClick={() => setSelectedYear(y => y - 1)} className="h-8 w-8">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-lg font-bold text-zinc-50 min-w-[60px] text-center">{selectedYear}</span>
            <Button variant="ghost" size="icon" onClick={() => setSelectedYear(y => y + 1)} className="h-8 w-8">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Month Selector */}
      <div className="flex flex-wrap items-center gap-1 bg-zinc-900/50 rounded-lg p-2 border border-zinc-800">
        {MONTH_NAMES.map((name, idx) => (
          <Button 
            key={idx}
            variant={selectedMonth === idx + 1 ? "secondary" : "ghost"} 
            size="sm" 
            onClick={() => setSelectedMonth(idx + 1)}
            className={cn("text-xs px-3 h-8", selectedMonth === idx + 1 && "bg-purple-600 text-white")}
          >
            {name}
          </Button>
        ))}
      </div>

      <Badge className="bg-purple-600 text-white text-sm px-3 py-1">
        {FULL_MONTH_NAMES[selectedMonth - 1]} {selectedYear}
      </Badge>

      {/* Cash Flow charts — last 6 months. Toggle Plan/Fact, separate income & expenses. */}
      <div className="space-y-3">
        {/* Plan/Fact toggle + by-manager switch */}
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm text-zinc-400">View:</span>
          <div className="inline-flex items-center bg-zinc-900 border border-zinc-800 rounded-md p-0.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setChartView("plan")}
              className={cn(
                "h-7 px-3 text-xs",
                chartView === "plan" ? "bg-blue-600 text-white hover:bg-blue-600" : "text-zinc-400 hover:text-zinc-50"
              )}
            >
              Plan
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setChartView("fact")}
              className={cn(
                "h-7 px-3 text-xs",
                chartView === "fact" ? "bg-emerald-600 text-white hover:bg-emerald-600" : "text-zinc-400 hover:text-zinc-50"
              )}
            >
              Fact
            </Button>
          </div>
          <span className="text-xs text-zinc-500">
            {chartView === "plan"
              ? "All services booked for the month (paid + unpaid)"
              : "Money actually received in the month, whatever month it was booked for"}
          </span>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setExcludePartner((v) => !v)}
            data-testid="pnl-exclude-partner-toggle"
            className={cn(
              "h-7 px-3 text-xs border-zinc-700",
              excludePartner ? "bg-orange-600 text-white border-orange-600 hover:bg-orange-600" : "bg-zinc-900 text-zinc-400"
            )}
          >
            {excludePartner ? "Partner income excluded" : "Exclude partner income"}
          </Button>
          {excludePartner && (
            <span className="text-xs text-orange-400">
              Sales, income, profit &amp; margin shown without partner (owner) sales. Expenses unchanged.
            </span>
          )}

          <div className="ml-auto flex items-center gap-2">
            <span className="text-sm text-zinc-400">Income detail:</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setByManager((v) => !v)}
              className={cn(
                "h-7 px-3 text-xs border-zinc-700",
                byManager ? "bg-purple-600 text-white border-purple-600" : "bg-zinc-900 text-zinc-400"
              )}
            >
              <Users className="h-3 w-3 mr-1.5" />
              {byManager ? "By manager" : "By category"}
            </Button>
          </div>
        </div>

        {/* === AGGREGATE: Cash flow line chart (income / expenses / net) === */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold text-zinc-50 flex items-center gap-2">
              <Activity className="h-4 w-4 text-emerald-500" />
              Cash flow · last 6 months ({chartView})
            </CardTitle>
            <div className="flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1.5 text-emerald-400">
                <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" /> Income
              </span>
              <span className="flex items-center gap-1.5 text-red-400">
                <span className="inline-block w-2 h-2 rounded-full bg-red-500" /> Expenses
              </span>
              <span className="flex items-center gap-1.5 text-orange-400">
                <span className="inline-block w-2 h-2 rounded-full bg-orange-500" /> Net
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={cashFlow6m} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="pnlAggIncomeFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="pnlAggExpensesFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ef4444" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                  <XAxis dataKey="month" stroke="#71717a" fontSize={12} />
                  <YAxis stroke="#71717a" fontSize={12} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                  <Tooltip
                    contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a", borderRadius: "6px", fontSize: "12px" }}
                    formatter={(value, name) => [formatUSD(value), name]}
                  />
                  <Area
                    type="monotone"
                    dataKey={
                      chartView === "plan"
                        ? (excludePartner ? "income_plan_np" : "income_plan")
                        : (excludePartner ? "income_fact_np" : "income_fact")
                    }
                    name={excludePartner ? "Income (no partner)" : "Income"}
                    stroke="#10b981"
                    strokeWidth={2}
                    fill="url(#pnlAggIncomeFill)"
                  />
                  <Area
                    type="monotone"
                    dataKey={chartView === "plan" ? "expenses_plan" : "expenses_fact"}
                    name="Expenses"
                    stroke="#ef4444"
                    strokeWidth={2}
                    fill="url(#pnlAggExpensesFill)"
                  />
                  <Line
                    type="monotone"
                    dataKey={(d) =>
                      chartView === "plan"
                        ? (d[excludePartner ? "income_plan_np" : "income_plan"] || 0) - (d.expenses_plan || 0)
                        : (d[excludePartner ? "income_fact_np" : "income_fact"] || 0) - (d.expenses_fact || 0)
                    }
                    name="Net"
                    stroke="#f97316"
                    strokeWidth={2.5}
                    dot={{ fill: "#f97316", r: 4 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Income + Expenses charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* INCOME */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2">
              <CardTitle className="text-base font-semibold text-zinc-50 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-emerald-500" />
                Income · {byManager ? "by manager" : "by category"}
              </CardTitle>
              {!byManager && (
                <div className="flex items-center gap-3 text-xs">
                  <span className="flex items-center gap-1.5 text-emerald-400">
                    <span className="inline-block w-2 h-2 rounded-sm bg-emerald-500" /> Sales
                  </span>
                  {!excludePartner && (
                    <span className="flex items-center gap-1.5 text-orange-400">
                      <span className="inline-block w-2 h-2 rounded-sm bg-orange-500" /> Partner
                    </span>
                  )}
                  <span className="flex items-center gap-1.5 text-cyan-400">
                    <span className="inline-block w-2 h-2 rounded-sm bg-cyan-500" /> CRM
                  </span>
                </div>
              )}
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={byManager ? incomeByManager.rows : cashFlow6m}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                    <XAxis dataKey="month" stroke="#71717a" fontSize={12} />
                    <YAxis stroke="#71717a" fontSize={12} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                    <Tooltip
                      contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a", borderRadius: "6px", fontSize: "12px" }}
                      formatter={(value, name) => [formatUSD(value), name]}
                      cursor={{ fill: "#ffffff08" }}
                    />
                    {byManager && <Legend wrapperStyle={{ fontSize: "11px", paddingTop: "8px" }} />}
                    {byManager ? (
                      <>
                        {incomeByManager.managers.map((mgr, i) => (
                          <Bar
                            key={mgr}
                            dataKey={mgr}
                            name={mgr}
                            stackId="income"
                            fill={MANAGER_COLORS[i % MANAGER_COLORS.length]}
                            radius={i === incomeByManager.managers.length - 1 && /* CRM is on top */ false ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                          />
                        ))}
                        <Bar
                          dataKey="CRM (recurring)"
                          name="CRM (recurring)"
                          stackId="income"
                          fill="#71717a"
                          radius={[4, 4, 0, 0]}
                        />
                      </>
                    ) : (
                      <>
                        <Bar
                          dataKey={chartView === "plan" ? "sales_plan" : "sales_fact"}
                          name="Sales"
                          stackId="income"
                          fill="#10b981"
                        />
                        {!excludePartner && (
                          <Bar
                            dataKey={chartView === "plan" ? "partner_plan" : "partner_fact"}
                            name="Partner (owner)"
                            stackId="income"
                            fill="#f97316"
                          />
                        )}
                        <Bar
                          dataKey={chartView === "plan" ? "crm_plan" : "crm_fact"}
                          name="CRM"
                          stackId="income"
                          fill="#06b6d4"
                        />
                        <Bar
                          dataKey={chartView === "plan" ? "support_plan" : "support_fact"}
                          name="Support"
                          stackId="income"
                          fill="#2dd4bf"
                          radius={[4, 4, 0, 0]}
                        />
                      </>
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* EXPENSES */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base font-semibold text-zinc-50 flex items-center gap-2">
                <TrendingDown className="h-4 w-4 text-red-500" />
                Expenses · last 6 months
              </CardTitle>
              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1.5 text-orange-400">
                  <span className="inline-block w-2 h-2 rounded-sm bg-orange-500" /> Salary
                </span>
                <span className="flex items-center gap-1.5 text-violet-400">
                  <span className="inline-block w-2 h-2 rounded-sm bg-violet-500" /> Artists
                </span>
                <span className="flex items-center gap-1.5 text-pink-400">
                  <span className="inline-block w-2 h-2 rounded-sm bg-pink-500" /> AD
                </span>
                <span className="flex items-center gap-1.5 text-zinc-400">
                  <span className="inline-block w-2 h-2 rounded-sm bg-zinc-500" /> Other
                </span>
                <span className="flex items-center gap-1.5 text-sky-400">
                  <span className="inline-block w-2 h-2 rounded-sm bg-sky-500" /> Ads
                </span>
                {chartView === "plan" && (
                  <span className="flex items-center gap-1.5 text-red-400">
                    <span className="inline-block w-2 h-2 rounded-sm bg-red-600" /> Bad debt
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={cashFlow6m} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                    <XAxis dataKey="month" stroke="#71717a" fontSize={12} />
                    <YAxis stroke="#71717a" fontSize={12} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                    <Tooltip
                      contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a", borderRadius: "6px", fontSize: "12px" }}
                      formatter={(value, name) => [formatUSD(value), name]}
                      cursor={{ fill: "#ffffff08" }}
                    />
                    <Bar
                      dataKey={chartView === "plan" ? "salary_plan" : "salary_fact"}
                      name="Salary"
                      stackId="expenses"
                      fill="#f97316"
                    />
                    <Bar
                      dataKey={chartView === "plan" ? "artist_plan" : "artist_fact"}
                      name="Artists"
                      stackId="expenses"
                      fill="#a78bfa"
                    />
                    <Bar
                      dataKey={chartView === "plan" ? "ad_fee_plan" : "ad_fee_fact"}
                      name="AD fees"
                      stackId="expenses"
                      fill="#ec4899"
                    />
                    <Bar
                      dataKey={chartView === "plan" ? "other_plan" : "other_fact"}
                      name="Other"
                      stackId="expenses"
                      fill="#71717a"
                    />
                    <Bar
                      dataKey={chartView === "plan" ? "ads_plan" : "ads_fact"}
                      name="Ad spend"
                      stackId="expenses"
                      fill="#0ea5e9"
                      radius={chartView === "plan" ? undefined : [4, 4, 0, 0]}
                    />
                    {chartView === "plan" && (
                      <Bar dataKey="bad_debt_plan" name="Bad debt (90+ unpaid)" stackId="expenses" fill="#dc2626" radius={[4, 4, 0, 0]} />
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Summary Cards — from backend, identical to Owner Dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="pnl-summary-cards">
        <Card className="bg-blue-900/20 border-blue-800/30">
          <CardContent className="p-3">
            <div className="text-xs text-blue-400 mb-1">Income Plan</div>
            <p className="text-xl font-bold text-blue-400" data-testid="pnl-income-plan">{formatUSD(summary.income_plan)}</p>
            <p className="text-[10px] text-zinc-500 mt-0.5">Sales: {formatUSD(summary.sales_plan)} + CRM: {formatUSD(summary.crm_plan)} + Support: {formatUSD(summary.support_plan)}</p>
          </CardContent>
        </Card>
        <Card className="bg-green-900/20 border-green-800/30">
          <CardContent className="p-3">
            <div className="text-xs text-green-400 mb-1">Income Fact</div>
            <p className="text-xl font-bold text-green-400" data-testid="pnl-income-fact">{formatUSD(summary.income_fact)}</p>
            <p className="text-[10px] text-zinc-500 mt-0.5">Sales: {formatUSD(summary.sales_fact)} + CRM: {formatUSD(summary.crm_fact)} + Support: {formatUSD(summary.support_fact)}</p>
          </CardContent>
        </Card>
        <Card className="bg-orange-900/20 border-orange-800/30">
          <CardContent className="p-3">
            <div className="text-xs text-orange-400 mb-1">Expenses Plan</div>
            <p className="text-xl font-bold text-orange-400" data-testid="pnl-expenses-plan">{formatUSD(summary.expenses_plan)}</p>
            <p className="text-[10px] text-zinc-500 mt-0.5">Sal: {formatUSD(expBreakdown.salaries_plan)} Art: {formatUSD(expBreakdown.artist_plan)} AD: {formatUSD(expBreakdown.ad_fee_plan)} Other: {formatUSD(expBreakdown.other_plan)} Ads: {formatUSD(expBreakdown.ads_plan)}
              {(expBreakdown.bad_debt_plan || 0) > 0 && (
                <span
                  className="text-red-400"
                  data-testid="pnl-bad-debt-plan"
                  title={(expBreakdown.bad_debt_items || []).map((it) => `${it.project_name || it.name} — ${formatUSD(it.amount_usd)} (${it.days}d)`).join("\n")}
                >
                  {" "}Bad debt: {formatUSD(expBreakdown.bad_debt_plan)}
                </span>
              )}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-red-900/20 border-red-800/30">
          <CardContent className="p-3">
            <div className="text-xs text-red-400 mb-1">Expenses Fact</div>
            <p className="text-xl font-bold text-red-400" data-testid="pnl-expenses-fact">{formatUSD(summary.expenses_fact)}</p>
            <p className="text-[10px] text-zinc-500 mt-0.5">Sal: {formatUSD(expBreakdown.salaries_fact)} Art: {formatUSD(expBreakdown.artist_fact)} AD: {formatUSD(expBreakdown.ad_fee_fact)} Other: {formatUSD(expBreakdown.other_fact)} Ads: {formatUSD(expBreakdown.ads_fact)}</p>
          </CardContent>
        </Card>
        <Card className={cn("border", (summary.profit_plan || 0) >= 0 ? "bg-purple-900/20 border-purple-800/30" : "bg-red-900/20 border-red-800/30")}>
          <CardContent className="p-3">
            <div className={cn("text-xs mb-1", (summary.profit_plan || 0) >= 0 ? "text-purple-400" : "text-red-400")}>Profit Plan</div>
            <p className={cn("text-xl font-bold", (summary.profit_plan || 0) >= 0 ? "text-purple-400" : "text-red-400")} data-testid="pnl-profit-plan">{formatUSD(summary.profit_plan)}</p>
          </CardContent>
        </Card>
        <Card className={cn("border", (summary.profit_fact || 0) >= 0 ? "bg-emerald-900/20 border-emerald-800/30" : "bg-red-900/20 border-red-800/30")}>
          <CardContent className="p-3">
            <div className={cn("text-xs mb-1", (summary.profit_fact || 0) >= 0 ? "text-emerald-400" : "text-red-400")}>Profit Fact</div>
            <p className={cn("text-xl font-bold", (summary.profit_fact || 0) >= 0 ? "text-emerald-400" : "text-red-400")} data-testid="pnl-profit-fact">{formatUSD(summary.profit_fact)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Margin */}
      {summary.margin !== undefined && (
        <div className="flex items-center gap-3">
          <Percent className="h-4 w-4 text-cyan-400" />
          <span className="text-sm text-zinc-400">Margin:</span>
          <Badge variant="outline" className={cn("text-sm",
            (summary.margin || 0) >= 30 && "bg-green-500/10 text-green-400 border-green-500/30",
            (summary.margin || 0) >= 0 && (summary.margin || 0) < 30 && "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
            (summary.margin || 0) < 0 && "bg-red-500/10 text-red-400 border-red-500/30"
          )} data-testid="pnl-margin">
            {(summary.margin || 0).toFixed(1)}%
          </Badge>
        </div>
      )}

      {/* Detail Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="sales" className="data-[state=active]:bg-zinc-700">
            <Briefcase className="h-4 w-4 mr-2" /> Sales
          </TabsTrigger>
          <TabsTrigger value="crm" className="data-[state=active]:bg-zinc-700">
            <Building2 className="h-4 w-4 mr-2" /> CRM
          </TabsTrigger>
          <TabsTrigger value="salaries" className="data-[state=active]:bg-zinc-700">
            <Users className="h-4 w-4 mr-2" /> Salaries
          </TabsTrigger>
          <TabsTrigger value="artists" className="data-[state=active]:bg-zinc-700">
            <Palette className="h-4 w-4 mr-2" /> Artists
          </TabsTrigger>
          <TabsTrigger value="other" className="data-[state=active]:bg-zinc-700">
            <Wallet className="h-4 w-4 mr-2" /> Other
          </TabsTrigger>
          <TabsTrigger value="ads" className="data-[state=active]:bg-zinc-700" data-testid="pnl-tab-ads">
            <Megaphone className="h-4 w-4 mr-2" /> Ad spend
          </TabsTrigger>
        </TabsList>

        {/* SALES TAB */}
        <TabsContent value="sales">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-zinc-50 flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-blue-400" />
                Sales - {FULL_MONTH_NAMES[selectedMonth - 1]} {selectedYear}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <Card className="bg-zinc-800/50 border-zinc-700">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="h-5 w-5 text-blue-400" />
                      <span className="text-sm text-zinc-400">PLAN (prob &ge; 50%)</span>
                    </div>
                    <p className="text-3xl font-bold text-blue-400">{formatUSD(summary.sales_plan)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-emerald-900/20 border-emerald-700/30">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                      <span className="text-sm text-zinc-400">FACT (PAID)</span>
                    </div>
                    <p className="text-3xl font-bold text-emerald-400">{formatUSD(summary.sales_fact)}</p>
                  </CardContent>
                </Card>
              </div>

              <div className="overflow-x-auto max-h-96">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800">
                      <TableHead className="text-zinc-400">Service</TableHead>
                      <TableHead className="text-zinc-400">Client</TableHead>
                      <TableHead className="text-zinc-400 text-right">Amount</TableHead>
                      <TableHead className="text-zinc-400 text-center">Booked</TableHead>
                      <TableHead className="text-zinc-400 text-center">Prob %</TableHead>
                      <TableHead className="text-zinc-400 text-center">In Plan</TableHead>
                      <TableHead className="text-zinc-400 text-center">Paid</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {salesRecords.map((s, idx) => (
                      <TableRow key={idx} className="border-zinc-800">
                        <TableCell className="text-zinc-50">{s.service_type || "—"}</TableCell>
                        <TableCell className="text-zinc-400">{s.client_name || "—"}</TableCell>
                        <TableCell className="text-right font-mono text-zinc-300">
                          {s.currency === "USD" ? "$" : s.currency === "EUR" ? "€" : "₴"}{(s.amount || 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-center text-xs font-mono">
                          {s.month === currentMonthKey
                            ? <span className="text-zinc-500">—</span>
                            : <span className="text-amber-400" title={`Booked for ${s.month}, paid ${s.paid_date?.slice(0, 10) || "—"}`}>{s.month || "—"}</span>}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={cn("text-xs", (s.probability || 0) >= 50 ? "bg-blue-600" : "bg-zinc-700")}>{s.probability || 0}%</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {s._inPlan ? <CheckCircle2 className="h-4 w-4 text-blue-400 mx-auto" /> : "—"}
                        </TableCell>
                        <TableCell className="text-center">
                          {s._inFact
                            ? <CheckCircle2 className="h-4 w-4 text-emerald-400 mx-auto" />
                            : s._paidMonth
                              ? <span className="text-[10px] font-mono text-zinc-500" title={`Paid ${s.paid_date?.slice(0, 10) || s._paidMonth} — counts in ${s._paidMonth}`}>{s._paidMonth}</span>
                              : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                    {salesRecords.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center text-zinc-500 py-8">No sales for this month</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CRM TAB */}
        <TabsContent value="crm">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-zinc-50 flex items-center gap-2">
                <Building2 className="h-5 w-5 text-cyan-400" />
                CRM Payments - {FULL_MONTH_NAMES[selectedMonth - 1]} {selectedYear}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <Card className="bg-zinc-800/50 border-zinc-700">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="h-5 w-5 text-cyan-400" />
                      <span className="text-sm text-zinc-400">PLAN</span>
                    </div>
                    <p className="text-3xl font-bold text-cyan-400">{formatUSD(summary.crm_plan)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-emerald-900/20 border-emerald-700/30">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                      <span className="text-sm text-zinc-400">PAID</span>
                    </div>
                    <p className="text-3xl font-bold text-emerald-400">{formatUSD(summary.crm_fact)}</p>
                  </CardContent>
                </Card>
              </div>

              <div className="overflow-x-auto max-h-96">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800">
                      <TableHead className="text-zinc-400">Client</TableHead>
                      <TableHead className="text-zinc-400 text-right">Planned</TableHead>
                      <TableHead className="text-zinc-400 text-right">Actual</TableHead>
                      <TableHead className="text-zinc-400 text-center">Booked</TableHead>
                      <TableHead className="text-zinc-400 text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {crmRecords.map((p, idx) => (
                      <TableRow key={idx} className="border-zinc-800">
                        <TableCell className="text-zinc-50">{p.client_name || "—"}</TableCell>
                        <TableCell className="text-right font-mono text-cyan-400">${(p.planned_amount || 0).toLocaleString()}</TableCell>
                        <TableCell className="text-right font-mono text-emerald-400">{p.actual_amount ? `$${p.actual_amount.toLocaleString()}` : "—"}</TableCell>
                        <TableCell className="text-center text-xs font-mono">
                          {p.month === currentMonthKey
                            ? <span className="text-zinc-500">—</span>
                            : <span className="text-amber-400" title={`Booked for ${p.month}, paid ${p.payment_date?.slice(0, 10) || "—"}`}>{p.month || "—"}</span>}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge
                            className={cn("text-xs", p._inFact ? "bg-emerald-600" : "bg-zinc-700")}
                            title={p._paidMonth && !p._inFact ? `Paid ${p.payment_date?.slice(0, 10) || p._paidMonth} — counts in ${p._paidMonth}` : undefined}
                          >
                            {p._paidMonth && !p._inFact ? `paid · ${p._paidMonth}` : p.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {crmRecords.length === 0 && (
                      <TableRow><TableCell colSpan={5} className="text-center text-zinc-500 py-8">No CRM payments for this month</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SALARIES TAB */}
        <TabsContent value="salaries">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-zinc-50 flex items-center gap-2">
                <Users className="h-5 w-5 text-orange-400" />
                Team Salaries - {FULL_MONTH_NAMES[selectedMonth - 1]} {selectedYear}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <Card className="bg-zinc-800/50 border-zinc-700">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="h-5 w-5 text-orange-400" />
                      <span className="text-sm text-zinc-400">PLAN</span>
                    </div>
                    <p className="text-3xl font-bold text-orange-400">{formatUSD(expBreakdown.salaries_plan)}</p>
                    <p className="text-xs text-zinc-500 mt-1">{salaryRecords.length} employees</p>
                  </CardContent>
                </Card>
                <Card className="bg-red-900/20 border-red-700/30">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="h-5 w-5 text-red-400" />
                      <span className="text-sm text-zinc-400">PAID</span>
                    </div>
                    <p className="text-3xl font-bold text-red-400">{formatUSD(expBreakdown.salaries_fact)}</p>
                  </CardContent>
                </Card>
              </div>

              <div className="overflow-x-auto max-h-96">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800">
                      <TableHead className="text-zinc-400">Employee</TableHead>
                      <TableHead className="text-zinc-400 text-right">Amount</TableHead>
                      <TableHead className="text-zinc-400 text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {salaryRecords.map((s, idx) => (
                      <TableRow key={idx} className="border-zinc-800">
                        <TableCell className="text-zinc-50">{s.employee_name || "—"}</TableCell>
                        <TableCell className="text-right font-mono text-zinc-300">
                          {s.currency === "USD" ? "$" : s.currency === "EUR" ? "€" : "₴"}{(s.amount || 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={cn("text-xs", s.status === "paid" ? "bg-emerald-600" : "bg-zinc-700")}>{s.status}</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {salaryRecords.length === 0 && (
                      <TableRow><TableCell colSpan={3} className="text-center text-zinc-500 py-8">No salary payments for this month</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ARTISTS TAB */}
        <TabsContent value="artists">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-zinc-50 flex items-center gap-2">
                <Palette className="h-5 w-5 text-pink-400" />
                Artist & AD Fee Payments - {FULL_MONTH_NAMES[selectedMonth - 1]} {selectedYear}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-zinc-800/50 border-zinc-700">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Palette className="h-5 w-5 text-pink-400" />
                      <span className="text-sm text-zinc-400">Artists Plan</span>
                    </div>
                    <p className="text-2xl font-bold text-pink-400">{formatUSD(expBreakdown.artist_plan)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-violet-900/20 border-violet-700/30">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="h-5 w-5 text-violet-400" />
                      <span className="text-sm text-zinc-400">AD Fee Plan</span>
                    </div>
                    <p className="text-2xl font-bold text-violet-400">{formatUSD(expBreakdown.ad_fee_plan)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-red-900/20 border-red-700/30">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="h-5 w-5 text-red-400" />
                      <span className="text-sm text-zinc-400">Total Paid</span>
                    </div>
                    <p className="text-2xl font-bold text-red-400">{formatUSD((expBreakdown.artist_fact || 0) + (expBreakdown.ad_fee_fact || 0))}</p>
                  </CardContent>
                </Card>
              </div>

              {/* External Paid records */}
              {artistDetail.paidExternal.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-emerald-400 mb-3">External Paid ({artistDetail.paidExternal.length})</h4>
                  <div className="overflow-x-auto max-h-64">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-zinc-800">
                          <TableHead className="text-zinc-400">Artist</TableHead>
                          <TableHead className="text-zinc-400 text-right">Amount</TableHead>
                          <TableHead className="text-zinc-400">Paid Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {artistDetail.paidExternal.map((e, idx) => (
                          <TableRow key={idx} className="border-zinc-800">
                            <TableCell className="text-zinc-50">{e.payee_name || e.payee_name_en || "—"}</TableCell>
                            <TableCell className="text-right font-mono text-emerald-400">
                              {e.currency === "USD" ? "$" : e.currency === "EUR" ? "€" : "₴"}{(e.amount || 0).toLocaleString()}
                            </TableCell>
                            <TableCell className="text-zinc-400">{e.paid_date?.slice(0, 10)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Task-Based Paid records */}
              {artistDetail.paidTask.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-sky-400 mb-3">Task-Based Paid ({artistDetail.paidTask.length})</h4>
                  <div className="overflow-x-auto max-h-64">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-zinc-800">
                          <TableHead className="text-zinc-400">Artist</TableHead>
                          <TableHead className="text-zinc-400">Task</TableHead>
                          <TableHead className="text-zinc-400 text-right">Amount</TableHead>
                          <TableHead className="text-zinc-400">Paid Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {artistDetail.paidTask.map((tp, idx) => (
                          <TableRow key={idx} className="border-zinc-800">
                            <TableCell className="text-zinc-50">{tp.artist_name || "—"}</TableCell>
                            <TableCell className="text-zinc-300 text-sm">{tp.task_name || "—"}</TableCell>
                            <TableCell className="text-right font-mono text-sky-400">
                              {tp.currency === "UAH" ? "₴" : tp.currency === "EUR" ? "€" : "$"}{(tp.amount || 0).toLocaleString()}
                            </TableCell>
                            <TableCell className="text-zinc-400">{tp.paid_date?.slice(0, 10)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* AD Fee records */}
              {(artistDetail.paidAdFee.length > 0 || artistDetail.pendingAdFee.length > 0) && (
                <div>
                  <h4 className="text-sm font-medium text-violet-400 mb-3">AD Fee Payments ({artistDetail.paidAdFee.length + artistDetail.pendingAdFee.length})</h4>
                  <div className="overflow-x-auto max-h-64">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-zinc-800">
                          <TableHead className="text-zinc-400">Task</TableHead>
                          <TableHead className="text-zinc-400 text-right">Amount</TableHead>
                          <TableHead className="text-zinc-400 text-center">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {[...artistDetail.paidAdFee, ...artistDetail.pendingAdFee].map((tp, idx) => (
                          <TableRow key={idx} className="border-zinc-800">
                            <TableCell className="text-zinc-50">{tp.task_name || tp.artist_name || "—"}</TableCell>
                            <TableCell className="text-right font-mono text-violet-400">
                              {tp.currency === "UAH" ? "₴" : tp.currency === "EUR" ? "€" : "$"}{(tp.amount || 0).toLocaleString()}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge className={cn("text-xs", tp.status === "paid" ? "bg-emerald-600" : "bg-zinc-700")}>{tp.status || "pending"}</Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Pending records */}
              {(artistDetail.pendingExternal.length > 0 || artistDetail.pendingTask.length > 0) && (
                <div>
                  <h4 className="text-sm font-medium text-orange-400 mb-3">Pending ({artistDetail.pendingExternal.length + artistDetail.pendingTask.length})</h4>
                  <div className="overflow-x-auto max-h-64">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-zinc-800">
                          <TableHead className="text-zinc-400">Artist / Task</TableHead>
                          <TableHead className="text-zinc-400 text-right">Amount</TableHead>
                          <TableHead className="text-zinc-400 text-center">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {artistDetail.pendingExternal.map((e, idx) => (
                          <TableRow key={idx} className="border-zinc-800">
                            <TableCell className="text-zinc-50">{e.payee_name || e.payee_name_en || "—"}</TableCell>
                            <TableCell className="text-right font-mono text-orange-400">
                              {e.currency === "USD" ? "$" : e.currency === "EUR" ? "€" : "₴"}{(e.amount || 0).toLocaleString()}
                            </TableCell>
                            <TableCell className="text-center"><Badge className="text-xs bg-zinc-700">{e.status || "pending"}</Badge></TableCell>
                          </TableRow>
                        ))}
                        {artistDetail.pendingTask.map((tp, idx) => (
                          <TableRow key={`t-${idx}`} className="border-zinc-800">
                            <TableCell className="text-zinc-50">{tp.artist_name || tp.task_name || "—"}</TableCell>
                            <TableCell className="text-right font-mono text-pink-400">
                              {tp.currency === "UAH" ? "₴" : tp.currency === "EUR" ? "€" : "$"}{(tp.amount || 0).toLocaleString()}
                            </TableCell>
                            <TableCell className="text-center"><Badge className="text-xs bg-zinc-700">{tp.status || "pending"}</Badge></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {artistDetail.paidExternal.length === 0 && artistDetail.pendingExternal.length === 0 && 
               artistDetail.paidTask.length === 0 && artistDetail.pendingTask.length === 0 &&
               artistDetail.paidAdFee.length === 0 && artistDetail.pendingAdFee.length === 0 && (
                <p className="text-zinc-500 text-center py-8">No artist or AD fee payments for this month</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* OTHER EXPENSES TAB */}
        <TabsContent value="other">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-zinc-50 flex items-center gap-2">
                <Wallet className="h-5 w-5 text-amber-400" />
                Other Expenses - {FULL_MONTH_NAMES[selectedMonth - 1]} {selectedYear}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-zinc-800/50 border-zinc-700">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="h-5 w-5 text-amber-400" />
                      <span className="text-sm text-zinc-400">PLAN</span>
                    </div>
                    <p className="text-3xl font-bold text-amber-400">{formatUSD(expBreakdown.other_plan)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-amber-900/20 border-amber-700/30">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="h-5 w-5 text-amber-500" />
                      <span className="text-sm text-zinc-400">APPROVED</span>
                    </div>
                    <p className="text-3xl font-bold text-amber-500">{formatUSD(expBreakdown.other_approved || 0)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-green-900/20 border-green-700/30">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="h-5 w-5 text-green-400" />
                      <span className="text-sm text-zinc-400">PAID</span>
                    </div>
                    <p className="text-3xl font-bold text-green-400">{formatUSD(expBreakdown.other_fact)}</p>
                  </CardContent>
                </Card>
              </div>

              {otherExpRecords.length > 0 ? (
                <div className="overflow-x-auto max-h-96">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-zinc-800">
                        <TableHead className="text-zinc-400">Description</TableHead>
                        <TableHead className="text-zinc-400">Category</TableHead>
                        <TableHead className="text-zinc-400 text-right">Amount</TableHead>
                        <TableHead className="text-zinc-400 text-center">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {otherExpRecords.map((e, idx) => (
                        <TableRow key={idx} className="border-zinc-800">
                          <TableCell className="text-zinc-50">{e.description || e.name || e.notes || "—"}</TableCell>
                          <TableCell className="text-zinc-400">{e.category || e.expense_type || "—"}</TableCell>
                          <TableCell className="text-right font-mono text-zinc-300">
                            {e.currency === "USD" ? "$" : e.currency === "EUR" ? "€" : "₴"}{(e.amount || 0).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className={cn("text-xs",
                              e.status === "paid" ? "bg-emerald-600" :
                              e.status === "approved" ? "bg-amber-600" :
                              "bg-zinc-700"
                            )}>{e.status || "pending"}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-zinc-500 text-center py-8">No other expenses for this month</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AD SPEND TAB — straight from the ad accounts (cards billed directly) */}
        <TabsContent value="ads">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-zinc-50 flex items-center gap-2">
                <Megaphone className="h-5 w-5 text-sky-400" />
                Ad spend - {FULL_MONTH_NAMES[selectedMonth - 1]} {selectedYear}
              </CardTitle>
              <p className="text-xs text-zinc-500">
                What the ad accounts report as spent. They charge the card directly, so this is the expense itself.
              </p>
            </CardHeader>
            <CardContent>
              {(() => {
                const platforms = Object.entries(expBreakdown.ads_by_platform || {}).sort((a, b) => b[1] - a[1]);
                const unconverted = Object.entries(expBreakdown.ads_unconverted || {});
                return (
                  <div className="space-y-4" data-testid="pnl-ads-breakdown">
                    <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
                      <p className="text-3xl font-bold text-sky-400">{formatUSD(expBreakdown.ads_fact)}</p>
                      {platforms.map(([name, usd]) => (
                        <span key={name} className="text-sm text-zinc-400">
                          {name} <span className="font-mono text-zinc-200">{formatUSD(usd)}</span>
                        </span>
                      ))}
                    </div>
                    {platforms.length === 0 && (
                      <p className="text-zinc-500 text-sm">No ad spend recorded for this month</p>
                    )}
                    {unconverted.length > 0 && (
                      <p className="text-xs text-amber-400">
                        Not in the total (no exchange rate): {unconverted.map(([c, a]) => `${a.toLocaleString()} ${c}`).join(", ")}
                      </p>
                    )}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Profit Summary Block */}
      <Card className="bg-gradient-to-r from-zinc-900 to-zinc-800 border-zinc-700" data-testid="pnl-profit-summary">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 gap-8">
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-zinc-400 text-center">PLANNED</h4>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-xs text-zinc-500">Income</div>
                  <div className="text-lg font-bold text-blue-400">{formatUSD(summary.income_plan)}</div>
                </div>
                <div>
                  <div className="text-xs text-zinc-500">Expenses</div>
                  <div className="text-lg font-bold text-orange-400">{formatUSD(summary.expenses_plan)}</div>
                </div>
                <div>
                  <div className="text-xs text-zinc-500">Profit</div>
                  <div className={cn("text-lg font-bold", (summary.profit_plan || 0) >= 0 ? "text-purple-400" : "text-red-400")}>{formatUSD(summary.profit_plan)}</div>
                </div>
              </div>
            </div>
            <div className="space-y-3 border-l border-zinc-700 pl-8">
              <h4 className="text-sm font-semibold text-zinc-400 text-center">ACTUAL</h4>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-xs text-zinc-500">Income</div>
                  <div className="text-lg font-bold text-emerald-400">{formatUSD(summary.income_fact)}</div>
                </div>
                <div>
                  <div className="text-xs text-zinc-500">Expenses</div>
                  <div className="text-lg font-bold text-red-400">{formatUSD(summary.expenses_fact)}</div>
                </div>
                <div>
                  <div className="text-xs text-zinc-500">Profit</div>
                  <div className={cn("text-lg font-bold", (summary.profit_fact || 0) >= 0 ? "text-emerald-400" : "text-red-400")}>{formatUSD(summary.profit_fact)}</div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Income by Service Type */}
      {incomeByServiceType.length > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-zinc-50 text-base flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-cyan-400" />
              Income by Service Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {incomeByServiceType.slice(0, 12).map((t, idx) => {
                const totalPlan = incomeByServiceType.reduce((s, x) => s + x.plan, 0);
                const pct = totalPlan > 0 ? Math.round((t.plan / totalPlan) * 100) : 0;
                return (
                  <div key={idx} className="bg-zinc-800/50 rounded p-2 text-center">
                    <div className="text-xs text-zinc-400 truncate mb-1">{t.name.replace(/_/g, " ")}</div>
                    <div className="text-sm font-mono text-blue-400">{formatUSD(t.plan)}</div>
                    <div className="text-[10px] text-zinc-500">{pct}%</div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default GeneralPnlPage;
